import React from "react";
import {Race} from "../../../schemas/races/racesSchema";
import {Divider, IconButton, Paper, Stack, Typography, useTheme} from "@mui/material";
import dayjs from "dayjs";
import CustomRichTextEditor from "../../../components/input/CustomRichTextEditor";
import {getFetcher} from "../../../utils/api/fetchers";
import useSWR from "swr";
import {Attachment} from "../../../schemas/attachments/attachmentsSchema";
import AttachmentViewer from "../attachment/AttachmentViewer";
import {useAuth} from "../../../utils/hooks/useAuth";
import {useNavigate} from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import UserRole from "../../../schemas/user/roles";
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import useMediaQuery from "@mui/material/useMediaQuery";

interface RaceDetailsOverviewProps {
    race: Race;
}

const RaceDetailsOverview: React.FC<RaceDetailsOverviewProps> = ({race}) => {
    const {hasPermission} = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {data: attachments} = useSWR<Attachment[]>(`/races/${race.id}/attachments`, getFetcher);

    const formatTime = (time: string): string => {
        const [hours, minutes] = time.split(':');
        return `${hours}:${minutes}`;
    };

    return (
        <Paper elevation={2} sx={{p: {xs: 2, sm: 4}}} id={`race-${race.id}`}>
            <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    {isMobile ? (
                        <Stack direction="column" alignItems="left">
                            <Typography variant="h5" fontWeight="bold">
                                {race.name}
                            </Typography>
                            <Typography variant="h6">
                                {dayjs(race.date).format('DD.MM.YYYY')}
                            </Typography>
                        </Stack>
                    ) : (
                        <Typography variant="h5">
                            <strong>{race.name}</strong> – {dayjs(race.date).format('DD.MM.YYYY')}
                        </Typography>
                    )}
                    <Stack direction="row">
                        {((race.resultStatus === "PUBLISHED" || hasPermission(UserRole.ORGANIZER)) && race.status === "FINISHED") && (
                            <IconButton
                                title={race.resultStatus === "PUBLISHED" ? "Zobrazit výsledky" : "Zobrazit nepublikované výsledky"}
                                onClick={() => navigate(`/results?season=${race.raceSeason.year}&type=race&race=${race.id}`)}
                                size="large"
                            >
                                {race.resultStatus === "PUBLISHED" ? (
                                    <EmojiEventsIcon sx={{color: "#FFD700"}} fontSize="large"/>
                                ) : (
                                    <EmojiEventsIcon sx={{color: "#dc1212"}} fontSize="large"/>
                                )}
                            </IconButton>
                        )}
                        {hasPermission(UserRole.COMMODORE) && (
                            <IconButton
                                title="Upravit závod"
                                onClick={() => navigate(`/races/${race.id}`)}
                                color="info"
                                size="large"
                            >
                                <EditIcon/>
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
                <Stack spacing={1} sx={{px: 1}}>
                    {race.time && (<Stack direction="row">
                            <Typography variant="body1" sx={{minWidth: 150, flexShrink: 0}}>
                                Čas startu:
                            </Typography>
                            <Typography variant="body1">
                                {formatTime(race.time)}
                            </Typography>
                        </Stack>
                    )}
                    {race.location && (<Stack direction="row">
                            <Typography variant="body1" sx={{minWidth: 150, flexShrink: 0}}>
                                Místo konání:
                            </Typography>
                            <Typography variant="body1">
                                {race.location}
                            </Typography>
                        </Stack>
                    )}
                    {race.club && (<Stack direction="row">
                            <Typography variant="body1" sx={{minWidth: 150, flexShrink: 0}}>
                                Pořádající klub:
                            </Typography>
                            <Typography variant="body1">
                                {race.club.name}
                            </Typography>
                        </Stack>
                    )}
                    {race.description && (
                        <CustomRichTextEditor
                            style={{borderLeft: "1px solid lightgray", border: "none"}}
                            title="Popis:"
                            value={race.description}
                            readOnly
                        />
                    )}
                </Stack>
                {attachments && attachments.length > 0 && (
                    <Stack spacing={2} sx={{pt: 1, px: 1}} alignItems="center" divider={<Divider orientation="horizontal" flexItem/>}>
                        {attachments
                            .sort((b, a) => a.type.localeCompare(b.type))
                            .map((attachment, index) => (
                                <AttachmentViewer
                                    key={attachment.id}
                                    raceId={race.id}
                                    attachment={attachment}
                                />
                            ))
                        }
                    </Stack>
                )}
            </Stack>
        </Paper>
    )
        ;
}

export default RaceDetailsOverview;